import React, { Fragment, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Breadcrumbs, ToolTip } from "../../AbstractElements";
import { Button, Card, CardBody, CardHeader, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, Input, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import { getSaltOnSearch, addNewItem, addSaltNameApi, searchOTCMasterApi, createPlatformOTCItem } from "./PharmacyMedicineService";
import { useLoader } from "../../_helper/LoaderContext";
import { toast } from "react-toastify";
import { ArrowLeft, PlusCircle } from "react-feather";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import AsyncSelect from "react-select/async";
import { packageTypes, productForms } from "./UpdateMedicine";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";
import { otcProductForms, otcPackageTypes } from "./UpdateMedicine";
import InfoIcon from "../PharmacyStocks/components/InfoIcon";
import { ADD_ITEMS } from "../PharmacyStocks/stoctTableInfo";

const AddItem = () => {
	const { t } = useTranslation();
	const history = useNavigate();
	const { setLoading } = useLoader();
	const { pharmacyPOS } = usePharmacy();

	const [formData, setFormData] = useState({
		isOTC: 'false',
		brandName: "",
		saltName: "",
		saltId: "",
		manufacturer: "",
		package: "",
		productForm: "",
		quantity: "",
		regularPrice: "",
		sellingPrice: "",
		barcode: "",
		note: "",
		minAlertQuantity: "",
		currency: pharmacyPOS.baseCurrency || "INR",
		tax: ""
	});

	const [errors, setErrors] = useState({});
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedOption, setSelectedOption] = useState(null); // Track selected AsyncSelect option

	const disableScrollProps = useDisableScrollOnFocus();

	const [filterTooltipOpen, setFilterTooltipOpen] = useState(false);
	const toggleFilterTooltip = () => setFilterTooltipOpen(!filterTooltipOpen);

	// Modals state
	const [isSaltModalOpen, setSaltModalOpen] = useState(false);
	const [isOTCModalOpen, setOTCModalOpen] = useState(false);

	// OTC Modal states
	const [otcModalData, setOtcModalData] = useState({
		name: "",
		package: "",
		manufacturer: "",
		productForm: "",
		note: ""
	});

	// ✅ Load medicines on search - handles both Medicine and OTC
	const loadMedicines = useCallback(async (inputValue) => {
		if (!inputValue || inputValue.length < 3) {
			return [];
		}

		try {
			setLoading(true);
			const body = {
				search: inputValue,
				type: formData.isOTC === 'true' ? 'otc' : ''
			};

			const response = await getSaltOnSearch(body);
			const medicines = response.data.data || [];

			// Format based on type
			return medicines.map((m) => {
				if (formData.isOTC === 'true') {
					// OTC items
					return {
						label: m.name || m.salt_composition || "",
						value: m._id,
						raw: m,
					};
				} else {
					// Medicines
					return {
						label: m.name
							? `${m.name} [${m.salt_composition || ""}]`
							: m.salt_composition || m.salt_composition_new || "",
						value: m._id,
						raw: m,
					};
				}
			});

		} catch (error) {
			console.error("Error fetching medicines:", error);
			toast.warning(t("Failed to search items"));
			return [];
		} finally {
			setLoading(false);
		}
	}, [formData.isOTC, setLoading, t]);

	// Clear all form data
	const clearFormData = useCallback(() => {
		setFormData({
			isOTC: formData.isOTC, // Keep the isOTC value
			brandName: "",
			saltName: "",
			saltId: "",
			manufacturer: "",
			package: "",
			productForm: "",
			quantity: "",
			regularPrice: "",
			sellingPrice: "",
			barcode: "",
			note: "",
			minAlertQuantity: formData.minAlertQuantity, // Keep alert quantity
			currency: pharmacyPOS.baseCurrency || "INR",
			tax: ""
		});
		setSelectedOption(null); // Clear selected option
		setSearchQuery(""); // Clear search query
		setErrors({}); // Clear errors
	}, [formData.isOTC, formData.minAlertQuantity, pharmacyPOS.baseCurrency]);

	// Handle medicine search change
	const handleMedicineSearchChange = useCallback((newValue) => {
		setSearchQuery(newValue);
		// Clear form when user starts typing a new search
		if (newValue && newValue.length >= 3) {
			clearFormData();
		}
	}, [clearFormData]);

	// ✅ When a medicine/OTC is selected
	const handleSelectMedicine = useCallback((selectedOption) => {
		setSelectedOption(selectedOption);

		if (!selectedOption) {
			clearFormData();
			return;
		}

		const medicine = selectedOption.raw || {};

		if (formData.isOTC === 'true') {
			// OTC item selected
			setFormData(prev => ({
				...prev,
				brandName: medicine.name || "",
				saltName: medicine.salt_composition || medicine.salt_composition_new || "",
				saltId: medicine._id || "",
				manufacturer: medicine.manufacturer || "",
				package: medicine.package || "",
				productForm: medicine.product_form || "",
				regularPrice: medicine.mrp || "",
				quantity: medicine.package_qty || "",
				note: medicine.note || "",
				tax: medicine.tax || ""
			}));
		} else {
			// Medicine selected
			setFormData(prev => ({
				...prev,
				brandName: medicine.name || "",
				saltName: medicine.salt_composition || medicine.salt_composition_new || "",
				saltId: medicine._id || "",
				manufacturer: medicine.manufacturer || "",
				package: medicine.package || "",
				productForm: medicine.product_form || "",
				regularPrice: medicine.mrp || "",
				quantity: medicine.package_qty || "",
				note: medicine.note || "",
				tax: medicine.tax || ""
			}));
		}
	}, [formData.isOTC, clearFormData]);

	// ✅ Handle item type change
	const handleItemTypeChange = (e) => {
		const { value } = e.target;
		// First clear all form data
		setFormData({
			isOTC: value,
			brandName: "",
			saltName: "",
			saltId: "",
			manufacturer: "",
			package: "",
			productForm: "",
			quantity: "",
			regularPrice: "",
			sellingPrice: "",
			barcode: "",
			note: "",
			minAlertQuantity: formData.minAlertQuantity,
			currency: pharmacyPOS.baseCurrency || "INR",
			tax: ""
		});

		// Clear AsyncSelect
		setSelectedOption(null);
		setSearchQuery("");
		setErrors({});

		// Force AsyncSelect to reload with new type
		// if (selectedOption) {
		// 	// If there was a selected option, clear it
		// 	handleSelectMedicine(null);
		// }
	};

	// ✅ Handle input change
	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
	};

	// ✅ Validation
	const validate = () => {
		let newErrors = {};
		if (!formData.isOTC) newErrors.isOTC = "Item type is required";

		if (formData.isOTC === 'false') {
			// Medicine validation
			if (!formData.brandName) newErrors.brandName = "Brand name is required";
			if (!formData.saltName) newErrors.saltName = "Salt name is required";
		} else {
			// OTC validation
			if (!formData.brandName) newErrors.brandName = "Product name is required";
		}

		if (!formData.manufacturer) newErrors.manufacturer = "Manufacturer is required";
		if (!formData.package) newErrors.package = "Packaging Type is required";
		if (!formData.productForm) newErrors.productForm = "Product form is required";
		if (!formData.quantity || formData.quantity <= 0) newErrors.quantity = "Quantity must be greater than 0";
		if (!formData.regularPrice || formData.regularPrice <= 0) newErrors.regularPrice = "Price must be greater than 0";
		if (!formData.minAlertQuantity) newErrors.minAlertQuantity = "Minimum Alert quantity is required";
		if (!formData.currency) newErrors.currency = "Currency is required";
		return newErrors;
	};

	// ✅ Submit add
	const handleSubmit = async (e) => {
		e.preventDefault();
		const validationErrors = validate();
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			toast.warning(t("Please fill in all required fields"));
			return;
		}
		try {
			setLoading(true);

			const body = {
				clinicId: pharmacyPOS?.hospital_id,
				isOTC: formData.isOTC === 'true',
				brandName: formData.brandName,
				manufacturer: formData.manufacturer,
				package: formData.package,
				productForm: formData.productForm,
				packageQuantity: formData.quantity,
				regularPrice: formData.regularPrice,
				barcode: formData.barcode,
				note: formData.note,
				minQuantityAlert: formData.minAlertQuantity,
				currency: formData.currency,
				tax: formData.tax
			};

			// Include different fields based on type
			if (formData.isOTC === 'false') {
				// Medicine
				body.saltName = formData.saltName;
				body.medicationId = formData.saltId;
			} else {
				// OTC - use brandName as product name
				body.saltName = formData.saltName || formData.brandName;
			}

			const response = await addNewItem(body);
			toast.success(response.data.message);
			gotoList();
		} catch (error) {
			console.error("Error adding item:", error.response?.data?.message);
			// toast.warning(error.response?.data?.message || t("Failed to add item"));
		} finally {
			setLoading(false);
		}
	};

	const gotoList = () => {
		history(`${process.env.PUBLIC_URL}/items`);
	};

	// For Medicine modal (salt names)
	const [saltRows, setSaltRows] = useState([{ brandName: "", saltName: "" }]);
	const [productForm, setProductForm] = useState("");
	const [packageType, setPackageType] = useState("");
	const [saltModalError, setSaltModalError] = useState("");
	const [isSaltSubmitting, setSaltSubmitting] = useState(false);

	// For OTC modal
	const [otcModalError, setOtcModalError] = useState("");
	const [isOTCSubmitting, setOTCSubmitting] = useState(false);

	// Medicine Modal Functions
	const addSaltRow = () => {
		setSaltRows([...saltRows, { saltName: "" }]);
	};

	const removeSaltRow = (index) => {
		const updated = saltRows.filter((_, i) => i !== index);
		setSaltRows(updated.length ? updated : [{ saltName: "" }]);
	};

	const handleSaltRowChange = (index, field, value) => {
		const updated = [...saltRows];
		if (field === "brandName" && index > 0) {
			updated[0][field] = value;
		} else {
			updated[index][field] = value;
		}
		setSaltRows(updated);
	}

	const toggleSaltModal = () => {
		setSaltModalOpen(!isSaltModalOpen);
		setSaltModalError("");
		setSaltRows([{ brandName: "", saltName: "" }]);
		setPackageType("");
		setProductForm("");
	};

	const handleSaltModalSubmit = async (e) => {
		e.preventDefault();

		// ✅ Validation
		for (let i = 0; i < saltRows.length; i++) {
			if (!saltRows[0]?.brandName?.trim()) {
				setSaltModalError("Brand name is required");
				return;
			}
			if (!saltRows[i].saltName.trim()) {
				setSaltModalError(`Please fill all the salt names`);
				return;
			}
		}
		if (!productForm) {
			setSaltModalError("Package form is required");
			return;
		}

		const saltNames = saltRows.map(row => row.saltName);
		const saltname_without_dosage = saltNames.join(" + ");
		const saltname_with_dosage = saltNames.map(name => `${name}`).join(" + ");
		const body = {
			salt_composition: saltname_with_dosage,
			salt_composition_new: saltname_without_dosage,
			package: packageType,
			product_form: productForm,
			name: saltRows[0].brandName,
		};

		try {
			setLoading(true);
			setSaltSubmitting(true);
			const response = await addSaltNameApi(body);

			if (response.data.status === "success") {
				toast.success(response.data.message);
				toggleSaltModal();
				// Clear the search to force refresh
				setSearchQuery("");
				setSelectedOption(null);
				clearFormData();
			}
		} catch (error) {
			console.error("Error adding salt:", error);
			toast.warning(error.response?.data?.message || t("Failed to add medicine"));
		} finally {
			setLoading(false);
			setSaltSubmitting(false);
		}
	};

	// OTC Modal Functions
	const toggleOTCModal = () => {
		setOTCModalOpen(!isOTCModalOpen);
		setOtcModalError("");
		setOtcModalData({
			name: "",
			package: "",
			manufacturer: "",
			productForm: "",
			note: ""
		});
	};

	const handleOTCModalChange = (e) => {
		const { name, value } = e.target;
		setOtcModalData(prev => ({
			...prev,
			[name]: value
		}));
	};

	const handleOTCModalSubmit = async (e) => {
		e.preventDefault();

		// ✅ Validation
		if (!otcModalData.name.trim()) {
			setOtcModalError("Product name is required");
			return;
		}
		if (!otcModalData.manufacturer.trim()) {
			setOtcModalError("Manufacturer is required");
			return;
		}
		if (!otcModalData.package) {
			setOtcModalError("Packaging Type is required");
			return;
		}
		if (!otcModalData.productForm) {
			setOtcModalError("Product form is required");
			return;
		}

		try {
			setLoading(true);
			setOTCSubmitting(true);

			const body = {
				name: otcModalData.name,
				manufacturer: otcModalData.manufacturer,
				package: otcModalData.package,
				product_form: otcModalData.productForm,
				salt_composition: otcModalData.name,
				salt_composition_new: otcModalData.name,
				note: otcModalData.note,
				type: "otc"
			};

			const response = await createPlatformOTCItem(body);

			if (response.data.status === "success") {
				toast.success(response.data.message || t("OTC product created successfully"));
				toggleOTCModal();
				// Clear the search to force refresh
				setSearchQuery("");
				setSelectedOption(null);
				clearFormData();
			}
		} catch (error) {
			console.error("Error creating OTC product:", error);
			// toast.warning(error.response?.data?.message || t("Failed to create OTC product"));
		} finally {
			setLoading(false);
			setOTCSubmitting(false);
		}
	};

	// Helper functions
	const getSearchPlaceholder = () => {
		return formData.isOTC === 'true'
			? t("Search OTC products by name...")
			: t("Search medicines by name or salt composition...");
	};

	const getNoOptionsMessage = () => {
		return searchQuery.length >= 3
			? (formData.isOTC === 'true'
				? t("No OTC products found. Try another name or add a new one.")
				: t("No medicines found. Try another name or add a new one."))
			: t("Type at least 3 characters to search...");
	};

	const isDark = document.body.classList.contains("dark-only");

	return (
		<Fragment>
			<Breadcrumbs mainTitle={t("Add Item")} parent={t("Dashboard")} title={t("Add Item")} />
			<Row className="mt-3">
				<Col sm={12}>
					<Card>
						<CardHeader className="d-flex justify-content-end align-items-center">
							<button
								className="btn btn-primary d-flex align-items-center"
								style={{ color: "white", height: "40px" }}
								onClick={gotoList}
							>
								<ArrowLeft className="me-2" /> {t("Back")}
							</button>
						</CardHeader>
						<CardBody>
							<form onSubmit={handleSubmit} className="row g-3">
								<div className="col-md-12 mb-3">
									<div className="row">
										<label className="form-label fw-bold">{t("Item Type")}</label>
										<div className="col-md-2">
											<div className="radio radio-primary">
												<Input
													id="itemMedicine"
													type="radio"
													name="isOTC"
													value="false"
													checked={formData.isOTC === 'false'}
													onChange={handleItemTypeChange}
												/>
												<Label for="itemMedicine" className="ps-3">{t("Medicine")}</Label>
											</div>
										</div>
										<div className="col-md-2">
											<div className="radio radio-primary">
												<Input
													id="itemOtc"
													type="radio"
													name="isOTC"
													value="true"
													checked={formData.isOTC === 'true'}
													onChange={handleItemTypeChange}
												/>
												<Label for="itemOtc" className="ps-3">OTC</Label>
											</div>
										</div>
										{errors.isOTC && <div className="text-danger">{errors.isOTC}</div>}
									</div>
								</div>

								{/* Search Section - Common for both Medicine and OTC */}
								<div className="col-md-6">
									<label className="form-label">
										{formData.isOTC === 'true' ? t("Search OTC Product") : t("Search Medicine")}
										<span className="text-danger">*</span>&nbsp;
										<InfoIcon text={formData.isOTC === 'true' ? ADD_ITEMS.searchOTC : ADD_ITEMS.searchMedicine} />
									</label>
									<div className="input-group">
										<AsyncSelect
											className="flex-grow-1"
											cacheOptions
											loadOptions={loadMedicines}
											defaultOptions={[]}
											onChange={handleSelectMedicine}
											onInputChange={handleMedicineSearchChange}
											placeholder={getSearchPlaceholder()}
											noOptionsMessage={() => getNoOptionsMessage()}
											isClearable={true}
											value={selectedOption}
											styles={{
												container: (base) => ({
													...base,
													flex: 1,
												}),
												control: (base) => ({
													...base,
													minHeight: '38px',
												}),
											}}
											theme={(theme) => ({
												...theme,
												colors: {
													...theme.colors,
													primary25: isDark ? "#373737" : "#e6f7ffff",

												},
											})}
											key={formData.isOTC} // Force re-render when type changes
										/>
										<div className="input-group-append" style={{ cursor: "pointer" }}>
											<span
												className="input-group-text cursor-pointer"
												id="FilterToolTip"
												onClick={formData.isOTC === 'true' ? toggleOTCModal : toggleSaltModal}
											>
												<PlusCircle />
											</span>
											<ToolTip
												attrToolTip={{
													placement: 'top',
													isOpen: filterTooltipOpen,
													target: 'FilterToolTip',
													toggle: toggleFilterTooltip,
												}}
											>
												<strong>
													{formData.isOTC === 'true'
														? t("Add New OTC Product")
														: t("Add New Brand Name")}
												</strong>
											</ToolTip>
										</div>
									</div>
									{/* <small className="text-muted">
										{formData.isOTC === 'true'
											? t("Search for OTC products like bandages, supplements, etc.")
											: t("Search for medicines by brand name or salt composition")}
									</small> */}
								</div>

								{/* Brand Name / Product Name */}
								<div className="col-md-6">
									<label className="form-label">
										{formData.isOTC === 'true' ? t("Product Name") : t("Brand Name")}
										<span className="text-danger">*</span>
									</label>
									<input
										type="text"
										name="brandName"
										value={formData.brandName}
										onChange={handleChange}
										className="form-control"
										placeholder={formData.isOTC === 'true' ? t("Enter product name") : t("Enter brand name")}
									// readOnly={!!selectedOption} // Make read-only when selected from search
									/>
									{errors.brandName && <div className="text-danger">{errors.brandName}</div>}
									{selectedOption && (
										<small className="text-muted">
											{t("Filled from search selection. Edit if needed.")}
										</small>
									)}
								</div>

								{/* Salt Name / Composition - Only for Medicine */}
								{formData.isOTC === 'false' && (
									<div className="col-md-6">
										<label className="form-label">{t("Salt Name / Composition")}
											<span className="text-danger">*</span>
										</label>
										<input
											type="text"
											name="saltName"
											value={formData.saltName}
											onChange={handleChange}
											className="form-control"
											placeholder={t("Enter salt name or composition")}
										// readOnly={!!selectedOption}
										/>
										{errors.saltName && <div className="text-danger">{errors.saltName}</div>}
									</div>
								)}

								{/* Manufacturer */}
								<div className={`${formData.isOTC === 'false' ? 'col-md-6' : 'col-md-4'}`}>
									<label className="form-label">{t("Manufacturer")}
										<span className="text-danger">*</span>
									</label>
									<input
										type="text"
										name="manufacturer"
										value={formData.manufacturer}
										onChange={handleChange}
										className="form-control"
										placeholder={t("Enter manufacturer name")}
									// readOnly={!!selectedOption}
									/>
									{errors.manufacturer && <div className="text-danger">{errors.manufacturer}</div>}
								</div>

								{/* Package */}
								<div className="col-md-4">
									<label className="form-label">{t("Packaging Type")}
										<span className="text-danger">*</span>&nbsp;
										<InfoIcon text={ADD_ITEMS.packagingType} />
									</label>
									<input
										type="text"
										name="package"
										value={formData.package}
										onChange={handleChange}
										className="form-control"
										placeholder={t("e.g. Strip, Bottle, Box")}
									// readOnly={!!selectedOption}
									/>
									{errors.package && <div className="text-danger">{errors.package}</div>}
								</div>

								{/* Product Form */}
								<div className="col-md-4">
									<label className="form-label">{t("Product Form")}
										<span className="text-danger">*</span>&nbsp;
										<InfoIcon text={ADD_ITEMS.productForm} />
									</label>
									<input
										type="text"
										name="productForm"
										value={formData.productForm}
										onChange={handleChange}
										className="form-control"
										placeholder={t("e.g. Tablet, Capsule, Syrup")}
									// readOnly={!!selectedOption}
									/>
									{errors.productForm && <div className="text-danger">{errors.productForm}</div>}
								</div>

								{/* Quantity */}
								<div className="col-md-4">
									<label className="form-label">{t("Units per Pack")}
										<span className="text-danger">*</span>&nbsp;
										<InfoIcon text={ADD_ITEMS.unitQuantity} />
									</label>
									<input
										type="number"
										name="quantity"
										min={1}
										value={formData.quantity}
										onChange={handleChange}
										className="form-control"
										{...disableScrollProps}
										placeholder={t("e.g. 10, 30, 100")}
									/>
									{errors.quantity && <div className="text-danger">{errors.quantity}</div>}
									{/* <small className="text-muted">
										{t("Number of units in one package")}
									</small> */}
								</div>

								{/*Unit MRP */}
								<div className="col-md-4">
									<label className="form-label">{t("Pack MRP")}
										<span className="text-danger">*</span>
									</label>
									<input
										type="number"
										name="regularPrice"
										min="0"
										step="0.01"
										value={formData.regularPrice}
										onChange={handleChange}
										className="form-control"
										{...disableScrollProps}
										placeholder={t("Maximum Retail Price")}
									/>
									{errors.regularPrice && <div className="text-danger">{errors.regularPrice}</div>}
								</div>
								<div className="col-md-2">
									<label className="form-label">{t("Tax")}
										&nbsp;
										<InfoIcon text={ADD_ITEMS.tax} /></label>
									<input
										type="number"
										name="tax"
										min={0}
										value={formData.tax}
										onChange={handleChange}
										className="form-control"
										{...disableScrollProps}
										placeholder={t("Set tax percentage")}
									/>
								</div>
								{/* Currency */}
								<div className="col-md-2">
									<label className="form-label">{t("Currency")}</label>
									<select
										name="currency"
										value={formData.currency}
										onChange={handleChange}
										className="form-select"
										disabled
									>
										<option value="INR">INR (₹)</option>
										<option value="USD">USD ($)</option>
										<option value="EUR">EUR (€)</option>
									</select>
									{errors.currency && <div className="text-danger">{errors.currency}</div>}
								</div>

								{/* Alert Quantity */}
								<div className="col-md-4">
									<label className="form-label">{t("Minimum Stock Level (Alert)")}
										<span className="text-danger">*</span>&nbsp;
										<InfoIcon text={ADD_ITEMS.minimumAlertQuantity} />
									</label>
									<input
										type="number"
										name="minAlertQuantity"
										min={0}
										value={formData.minAlertQuantity}
										onChange={handleChange}
										className="form-control"
										{...disableScrollProps}
										placeholder={t("Set low stock alert quantity")}
									/>
									{errors.minAlertQuantity && <div className="text-danger">{errors.minAlertQuantity}</div>}
									{/* <small className="text-muted">
										{t("Alert when stock goes below this quantity")}
									</small> */}
								</div>
								{/* barcode */}
								<div className="col-md-4">
									<label className="form-label">{t("Bar Code")}
										{/* <span className="text-danger">*</span> */}
									</label>
									<input
										type="text"
										name="barcode"
										value={formData.barcode}
										onChange={handleChange}
										className="form-control"
										placeholder={t("Enter barcode number")}
									// readOnly={!!selectedOption}
									/>
									{errors.barcode && <div className="text-danger">{errors.barcode}</div>}
								</div>
								{/* Note */}
								<div className="col-md-8">
									<label className="form-label">{t("Notes")}</label>
									<textarea
										name="note"
										value={formData.note}
										onChange={handleChange}
										className="form-control"
										rows="2"
										placeholder={t("Add notes or special instructions")}
									/>
								</div>

								{/* Buttons */}
								<div className="col-md-12 d-flex justify-content-end gap-2 mt-4">
									<button className="btn btn-primary" type="submit">
										{t("Add Item")}
									</button>
									<Button color="secondary" onClick={gotoList}>
										{t("Cancel")}
									</Button>
									{/* <Button color="warning" onClick={clearFormData} type="button">
										{t("Clear Form")}
									</Button> */}
								</div>
							</form>
						</CardBody>
					</Card>
				</Col>
			</Row>

			{/* Medicine (Salt) Modal */}
			<Modal isOpen={isSaltModalOpen} toggle={toggleSaltModal} size="lg">
				<ModalHeader toggle={toggleSaltModal}>{t("Add New Medicine")}</ModalHeader>
				<form onSubmit={handleSaltModalSubmit}>
					<ModalBody>
						<div className="row mb-3">
							<div className="col-md-12">
								<label className="form-label">{t("Brand Name")}</label>
								<input
									type="text"
									className="form-control"
									value={saltRows[0]?.brandName || ""}
									onChange={(e) => handleSaltRowChange(0, "brandName", e.target.value)}
									disabled={isSaltSubmitting}
									placeholder={t("e.g., Crocin, Combiflam")}
								/>
							</div>
						</div>

						<div className="mb-2">
							<div className="d-flex justify-content-between align-items-center">
								<label className="form-label">{t("Salt Names")}</label>
								<Button
									color="success"
									size="sm"
									type="button"
									onClick={addSaltRow}
									disabled={isSaltSubmitting}
								>
									+ {t("Add Salt")}
								</Button>
							</div>
							<small className="text-muted">{t("Add multiple salt names for this brand")}</small>
						</div>

						{saltRows.map((row, index) => (
							<div className="row mb-3 align-items-end" key={index}>
								<div className="col-md-9">
									<label className="form-label">
										{t("Salt Name / Composition")} {saltRows.length > 1 ? `${index + 1}` : ""}
									</label>
									<input
										type="text"
										className="form-control"
										value={row.saltName}
										onChange={(e) => handleSaltRowChange(index, "saltName", e.target.value)}
										disabled={isSaltSubmitting}
										placeholder={t("e.g., Paracetamol, Ibuprofen")}
									/>
								</div>
								<div className="col-md-3">
									{saltRows.length > 1 && (
										<Button
											color="danger"
											className="mt-4"
											type="button"
											onClick={() => removeSaltRow(index)}
											disabled={isSaltSubmitting}
										>
											{t("Remove")}
										</Button>
									)}
								</div>
							</div>
						))}
						<div className="row mb-3">
							<div className="col-md-6">
								<label className="form-label">{t("Packaging Type")}</label>
								<select
									className="form-select"
									value={packageType}
									onChange={(e) => setPackageType(e.target.value)}
									disabled={isSaltSubmitting}
								>
									<option value="">{t("Select Packaging Type")}</option>
									{packageTypes.map((form) => (
										<option key={form.id} value={form.name}>
											{form.name}
										</option>
									))}
								</select>
							</div>
							<div className="col-md-6">
								<label className="form-label">{t("Product Form")}</label>
								<select
									className="form-select"
									value={productForm}
									onChange={(e) => setProductForm(e.target.value)}
									disabled={isSaltSubmitting}
								>
									<option value="">{t("Select Product Form")}</option>
									{productForms.map((form) => (
										<option key={form.id} value={form.name}>
											{form.name}
										</option>
									))}
								</select>
							</div>
						</div>

						{saltModalError && <div className="alert alert-danger dark fade show mt-2">{saltModalError}</div>}
					</ModalBody>
					<ModalFooter>
						<Button color="primary" type="submit" disabled={isSaltSubmitting}>
							{isSaltSubmitting ? t("Adding...") : t("Add Medicine")}
						</Button>
						<Button color="secondary" type="button" onClick={toggleSaltModal} disabled={isSaltSubmitting}>
							{t("Cancel")}
						</Button>
					</ModalFooter>
				</form>
			</Modal>

			{/* OTC Modal */}
			<Modal isOpen={isOTCModalOpen} toggle={toggleOTCModal} size="lg">
				<ModalHeader toggle={toggleOTCModal}>{t("Add New OTC Product")}</ModalHeader>
				<form onSubmit={handleOTCModalSubmit}>
					<ModalBody>
						<div className="row mb-3">
							<div className="col-md-12">
								<label className="form-label">{t("Product Name")} *</label>
								<input
									type="text"
									name="name"
									className="form-control"
									value={otcModalData.name}
									onChange={handleOTCModalChange}
									disabled={isOTCSubmitting}
									placeholder={t("e.g., Bandage, Protein Powder, Thermometer")}
								/>
							</div>
						</div>

						<div className="row mb-3">
							<div className="col-md-6">
								<label className="form-label">{t("Manufacturer")} *</label>
								<input
									type="text"
									name="manufacturer"
									className="form-control"
									value={otcModalData.manufacturer}
									onChange={handleOTCModalChange}
									disabled={isOTCSubmitting}
									placeholder={t("e.g., Johnson & Johnson, Dr. Trust")}
								/>
							</div>
							<div className="col-md-6">
								<label className="form-label">{t("Packaging Type")} *</label>
								<input
									type="text"
									name="package"
									className="form-control"
									value={otcModalData.package}
									onChange={handleOTCModalChange}
									disabled={isOTCSubmitting}
									placeholder={t("e.g. Strip, Bottle, Box")}
								/>
							</div>
						</div>

						<div className="row mb-3">
							<div className="col-md-12">
								<label className="form-label">{t("Product Form")} *</label>
								<input
									type="text"
									name="productForm"
									className="form-control"
									value={otcModalData.productForm}
									onChange={handleOTCModalChange}
									disabled={isOTCSubmitting}
									placeholder={t("Enter Product Form")}
								/>
							</div>
						</div>

						<div className="row mb-3">
							<div className="col-md-12">
								<label className="form-label">{t("Notes")}</label>
								<textarea
									name="note"
									className="form-control"
									value={otcModalData.note}
									onChange={handleOTCModalChange}
									disabled={isOTCSubmitting}
									rows="2"
									placeholder={t("Additional information about this product...")}
								/>
							</div>
						</div>

						{otcModalError && <div className="alert alert-danger dark fade show mt-2">{otcModalError}</div>}
					</ModalBody>
					<ModalFooter>
						<Button color="primary" type="submit" disabled={isOTCSubmitting}>
							{isOTCSubmitting ? t("Adding...") : t("Add OTC Product")}
						</Button>
						<Button color="secondary" type="button" onClick={toggleOTCModal} disabled={isOTCSubmitting}>
							{t("Cancel")}
						</Button>
					</ModalFooter>
				</form>
			</Modal>
		</Fragment>
	);
};

export default AddItem;